import React, { useEffect, useState } from "react";
import { Loader2, Search, UserPlus, Users } from "lucide-react";
import { message } from "antd";
import {
  EligibleDoctor,
  fetchEligibleDoctors,
  inviteDoctorToShift,
} from "../api/shiftInvitation";
import { findSuitableDoctorsOperationally } from "../api/operationalAi";
import { formatDisplayName } from "../utils/personNameUtil";
import { useSkillLevelNamingOptional } from "../contexts/SkillLevelNamingContext";
import { skillMinimumPhrase } from "../constants/skillLevelStateNaming";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import SocialAvatar from "./social/SocialAvatar";

interface InviteDoctorsModalProps {
  open: boolean;
  shiftsId: string;
  shiftDayIds?: string[];
  skillId?: string;
  skillLabel?: string;
  onClose: () => void;
}

type InviteSuggestion = {
  doctorId: string;
  initials: string;
  why: string;
  alreadyInvited: boolean;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const initialsOf = (name?: string) => {
  const parts = String(name || "")
    .replace(/^dr\.?\s+/i, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length > 1) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return (parts[0] || "DR").slice(0, 2).toUpperCase();
};

const InviteDoctorsModal: React.FC<InviteDoctorsModalProps> = ({
  open,
  shiftsId,
  shiftDayIds,
  skillId,
  skillLabel,
  onClose,
}) => {
  const naming = useSkillLevelNamingOptional();
  const skillPhrase = skillMinimumPhrase(
    skillId || skillLabel,
    naming?.namingState,
  );
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [doctors, setDoctors] = useState<EligibleDoctor[]>([]);
  const [suggestions, setSuggestions] = useState<InviteSuggestion[]>([]);
  const [inviteMessage, setInviteMessage] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [invitingId, setInvitingId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  const PAGE_SIZE = 50;

  const load = async (search = query, nextPage = 0, append = false) => {
    if (!shiftsId) {
      setDoctors([]);
      setLoadError("This shift is missing an id, so invite cannot load.");
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchEligibleDoctors(shiftsId, search, nextPage, PAGE_SIZE);
      setDoctors((current) => (append ? [...current, ...rows] : rows));
      setHasMore(rows.length >= PAGE_SIZE);
      setPage(nextPage);
    } catch (e: any) {
      if (!append) setDoctors([]);
      setLoadError(e?.message || "Could not load doctors in your hospital network.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setQuery("");
      setInviteMessage("");
      setLoadError(null);
      setDoctors([]);
      setSuggestions([]);
    }
  }, [open, shiftsId]);

  useEffect(() => {
    if (!open || !shiftsId) return;
    let cancelled = false;
    findSuitableDoctorsOperationally(shiftsId, 6)
      .then((result) => {
        if (cancelled) return;
        const rows = Array.isArray(result.data?.candidates)
          ? (result.data?.candidates as unknown[])
          : [];
        const next: InviteSuggestion[] = [];
        for (const raw of rows) {
          const item = asRecord(raw);
          const doctorId = String(item.doctorId || "").trim();
          if (!doctorId || item.alreadyInvited || item.alreadyApplied) continue;
          const state = String(item.eligibilityState || "").trim();
          if (state && state !== "ELIGIBLE" && state !== "ELIGIBLE_WITH_WARNING") {
            continue;
          }
          next.push({
            doctorId,
            initials: initialsOf(String(item.doctorName || "")),
            why: [
              item.favourite ? "Favourite" : "",
              item.workedBefore ? "Worked here" : "",
              item.preCredentialed ? "Pre-credentialed" : "",
              "Same specialty",
              skillPhrase,
            ]
              .filter(Boolean)
              .join(" · "),
            alreadyInvited: false,
          });
          if (next.length === 4) break;
        }
        setSuggestions(next);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, shiftsId, skillPhrase]);

  useEffect(() => {
    if (!open) return;
    const handle = window.setTimeout(() => {
      void load(query);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [open, query, shiftsId]);

  const invite = async (doctor: EligibleDoctor) => {
    setInvitingId(doctor.userId);
    try {
      await inviteDoctorToShift(shiftsId, doctor.userId, {
        invitationMessage: inviteMessage,
        shiftDayIds:
          shiftDayIds && shiftDayIds.length > 0 ? shiftDayIds : undefined,
      });
      message.success("Invite sent");
      void load(query);
    } catch (e: any) {
      message.error(e?.message || "Invite failed");
    } finally {
      setInvitingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        className="z-[200] flex max-h-[90vh] w-[95vw] max-w-2xl flex-col gap-0 overflow-hidden border-gray-200/80 bg-white p-0 [&>button.absolute]:hidden"
        overlayClassName="z-[190]"
        style={{ zIndex: 200 }}
      >
        <DialogHeader className="shrink-0 space-y-1 border-b border-gray-100 px-5 pb-4 pt-5 text-left sm:px-6">
          <DialogTitle className="text-lg font-bold text-gray-900">
            Invite doctors
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-600">
            {skillPhrase
              ? `Suggestions for this shift · ${skillPhrase}`
              : "Suggestions for this shift, then your hospital network."}
          </DialogDescription>
        </DialogHeader>

        <div className="shrink-0 space-y-3 border-b border-gray-50 bg-gray-50/40 px-5 py-4 sm:px-6">
          <div className="relative">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="search"
              placeholder="Search by name"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <textarea
            placeholder="Optional message for the doctor"
            value={inviteMessage}
            onChange={(e) => setInviteMessage(e.target.value)}
            rows={2}
            className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-6">
          {suggestions.length > 0 ? (
            <div className="mb-5">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-gray-500">
                Suggested
              </p>
              <div className="space-y-2">
                {suggestions.map((row) => (
                  <div
                    key={row.doctorId}
                    className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-3 py-3"
                  >
                    <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-xs font-bold text-gray-700">
                      {row.initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900">
                        {row.initials}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">{row.why}</p>
                    </div>
                    <Button
                      type="button"
                      disabled={row.alreadyInvited || invitingId === row.doctorId}
                      onClick={() => {
                        void invite({
                          userId: row.doctorId,
                          displayName: row.initials,
                        });
                        setSuggestions((current) =>
                          current.map((item) =>
                            item.doctorId === row.doctorId
                              ? { ...item, alreadyInvited: true }
                              : item,
                          ),
                        );
                      }}
                      className="shrink-0 rounded-xl"
                    >
                      {invitingId === row.doctorId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                          {row.alreadyInvited ? "Invited" : `Invite ${row.initials}`}
                        </>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {loading && doctors.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Loader2 size={28} className="mb-3 animate-spin text-indigo-600" />
              <p className="text-sm">Loading your hospital network…</p>
            </div>
          ) : loadError ? (
            <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-6 text-center">
              <p className="text-sm font-medium text-rose-800">{loadError}</p>
            </div>
          ) : doctors.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/50 py-14 text-center">
              <Users size={32} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm font-medium text-gray-700">
                No matching doctors in your network
              </p>
              <p className="mx-auto mt-1 max-w-xs text-xs text-gray-500">
                Favourite, pre-credential, or complete a shift with a doctor
                first. Search uses their name.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {doctors.map((doc) => {
                const name =
                  formatDisplayName(
                    doc.firstName,
                    doc.middleName,
                    doc.lastName,
                    doc.displayName,
                  ) || "Doctor";
                const blocked = doc.alreadyInvited || doc.alreadyApplied;
                return (
                  <div
                    key={doc.userId}
                    className="flex items-center gap-3 rounded-2xl border border-gray-100 bg-white px-3 py-3"
                  >
                    <SocialAvatar
                      src={doc.profileImage}
                      name={name}
                      userType="DOCTOR"
                      size={44}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-gray-900">
                        {name}
                      </p>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {doc.favorite ? (
                          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800">
                            Favourite
                          </span>
                        ) : null}
                        {doc.workedBefore ? (
                          <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800">
                            Worked here
                          </span>
                        ) : null}
                        {doc.precredential ? (
                          <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-800">
                            Pre-credentialed
                          </span>
                        ) : null}
                        {doc.matchScore != null ? (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                            Match {Math.round(doc.matchScore)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <Button
                      type="button"
                      disabled={blocked || invitingId === doc.userId}
                      onClick={() => void invite(doc)}
                      className="shrink-0 rounded-xl"
                    >
                      {invitingId === doc.userId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <UserPlus className="mr-1.5 h-3.5 w-3.5" />
                          {doc.alreadyApplied
                            ? "Applied"
                            : doc.alreadyInvited
                              ? "Invited"
                              : "Invite"}
                        </>
                      )}
                    </Button>
                  </div>
                );
              })}
              {hasMore ? (
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => void load(query, page + 1, true)}
                  className="w-full rounded-xl border border-gray-200 py-2 text-xs font-semibold text-gray-700"
                >
                  {loading ? "Loading…" : "Show more eligible doctors"}
                </button>
              ) : null}
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end border-t border-gray-100 bg-white px-5 py-3 sm:px-6">
          <Button variant="outline" onClick={onClose} className="rounded-xl">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InviteDoctorsModal;
